/**
 * The MR stage: everything a launch-monitor page needs that isn't its world.
 *
 * `range-mr` and `course-mr` are the same product wearing different scenery —
 * same passthrough compositing, same anchoring flow, same bridge, same menus,
 * same shot plumbing. All of that lives here. A page supplies its content (what
 * hangs under `worldRoot`), its menu rows, and what to do when a shot ends.
 *
 * ## The frame contract
 *
 * Everything virtual parents under `worldRoot`, built at identity so the physics
 * BVH bakes *sim* coordinates: **tee at the origin, downrange +Z**. The anchor
 * drives only `worldRoot.matrix`; physics never re-learns the world.
 *
 * A course page keeps that contract by inserting its own `courseRoot` under
 * `worldRoot` and re-posing it so the current lie sits at the sim origin facing
 * +Z. That means every stage-level consumer of the frame — the mask, the chase
 * camera, the yardage projection, the HUD — needs no idea a course is loaded.
 */
import {
  THREE,
  app,
  CourseKeyboardControls,
  GolfBall,
  UnitConversions,
  MeshLoader,
  FuseRenderer,
  FuseXRSession,
  AnchorController,
  XRShotControls,
  WorldMask,
  ShotStatsPanel,
  GroundStatsHUD,
  HeadLockedHint,
  ContactShadow,
  AudioPlayer,
  ImpactSound,
  WindAmbience,
  WristMenu,
  ShotTv,
  InfoPanel,
  ShotPerspectiveCamera,
  ActionMenu,
  ControllerRays,
  RoomBounds,
  CoursePlayer,
  setViewerPosition,
} from '@opengolfsim/fuse';
import type {
  PointerRay,
  InfoPage,
  WristMenuStatus,
  WristMenuButton,
  ActionMenuItem,
  ActionMenuSection,
  WindowRect,
  ImpactVoice,
} from '@opengolfsim/fuse';

/** FUSE Link serves the app and this loopback socket on the headset. */
const LOOPBACK_BRIDGE_URL = 'ws://127.0.0.1:9210/ws';

/**
 * Where to look for shots when nothing is specified.
 *
 * Whoever served this page is the best guess at who has the launch monitor, so
 * default to a same-origin socket. That's what lets the Pi host both halves and
 * hand players a URL with no `?bridge=` in it — and same-origin sidesteps mixed
 * content and Local Network Access entirely.
 *
 * Plain-`http:` origins keep the loopback default, which is exactly the FUSE
 * Link case: it serves over `http://localhost:9210` (localhost is a secure
 * context without TLS, which is why that path needs no certificates at all) and
 * its socket lives on that same port. So the protocol check alone routes FUSE
 * Link correctly, with no hostname special-casing.
 */
function defaultBridgeUrl(): string {
  if (typeof window === 'undefined') return LOOPBACK_BRIDGE_URL;
  const { protocol, host } = window.location;
  if (protocol !== 'https:') return LOOPBACK_BRIDGE_URL;
  return `wss://${host}/ws`; // host, not hostname — the port matters
}

export const DEFAULT_BRIDGE_URL = defaultBridgeUrl();

/**
 * Ball speed (mph) above which the chase-cam TV appears — roughly the 100 yd
 * mark ('8 Iron' qualifies, 'Wedge' doesn't). It has to be a launch-time
 * estimate: the physics is a forward integrator with no lookahead, and
 * stats.carry is only "distance so far", not crossing 100 yd until well over
 * halfway through a driver's flight.
 */
const TV_MIN_BALL_SPEED_MPH = 80;

/** Portal distance when no window is active (the historical default). */
const DEFAULT_PORTAL_DIST = 1.2;

/** How long the "window framed" announcement stays up, seconds. */
const WINDOW_HINT_SECONDS = 4;

export const GroundThudSound = '../sounds/thud.wav';

const sunColor = new THREE.Color('#fcfae9');

/** What a page plugs into the stage. Everything is optional but `buildContent`. */
export type MrStageConfig = {
  /** Action-menu title bar. */
  title: string;
  /** Practice preset the page starts on. */
  defaultPreset: string;
  /** Ball `waitTime` — how long the ball rests before `shotEnded` fires, ms. */
  ballWaitTime: number;

  /**
   * Build the page's world. Runs after `worldRoot`, the camera and the mask
   * exist, and must leave `stage.golfBall` set — the stage drives it but never
   * decides its parent or its terrain, both of which are the page's call.
   */
  buildContent: (stage: MrStage) => Promise<void>;

  /** Help-panel copy. Re-read on every redraw, so live state is fine. */
  helpPages: (stage: MrStage) => InfoPage[];
  /** Wrist-panel grid. The stage appends nothing — pages own the whole list. */
  wristButtons: (stage: MrStage) => WristMenuButton[];
  /** Action-menu sections. */
  menuSections: (stage: MrStage) => ActionMenuSection[];
  /** Action-menu right rail. Defaults to help / reload / exit. */
  menuRail?: (stage: MrStage) => ActionMenuItem[];

  /**
   * The standing line shown head-locked whenever the player is anchored and
   * idle. Defaults to the range's "Ready — …". A course page replaces it with
   * the hole it is playing: that is the information wanted at address, and this
   * is the only always-visible surface that can carry it (the turf HUD's tiles
   * are baked from shot stats).
   */
  readyHint?: (stage: MrStage) => string | undefined;

  /**
   * Page-specific menu ids. Return true to claim one; anything unclaimed falls
   * through to the stage's own handling.
   */
  onSelect?: (id: string, stage: MrStage) => boolean;

  /** Everything page-specific that has to follow the tee wherever it lands. */
  onTeePose?: (pose: { position: THREE.Vector3; yaw: number }, stage: MrStage) => void;

  /** A shot came to rest. The stage has already parked the TV freeze-frame. */
  onShotEnded?: (stage: MrStage) => void;
  /** Put the ball back where the next shot starts from. */
  setupNextShot?: (stage: MrStage) => void;

  /** Per-frame page work, after input and before the ball update. */
  onFrame?: (delta: number, stage: MrStage, head: HeadPose, sessionVisible: boolean) => void;
  /** Session ended — restore the desktop preview. */
  onSessionEnd?: (stage: MrStage) => void;
  /** `?sim=1` desktop mask preview is on. */
  onSimPreview?: (stage: MrStage) => void;

  /** Extra roots to warm up, beyond the stage's own panels and `worldRoot`. */
  extraWarmupRoots?: (stage: MrStage) => THREE.Object3D[];
  /**
   * A page-owned pointable surface, routed alongside the stage's own panels.
   *
   * Deliberately NOT modal: it is aimed at, not opened, so it never counts
   * toward `panelOpen()` and never vetoes a shot. That distinction is the whole
   * reason this exists — the course map is up for the entire round, and a map
   * that suppressed every swing would be worse than no map.
   *
   * It claims the trigger only on the frames a ray is actually resting on it.
   */
  extraRayPanel?: (stage: MrStage) => {
    object: THREE.Object3D;
    updateRay: (dt: number, rays: readonly PointerRay[]) => { rayIndex: number; distance: number } | undefined;
    /** The trigger, while a ray is on it. Return true if consumed. */
    activateHover: () => boolean;
    clearPointer?: () => void;
  } | undefined;

  /**
   * Keep the sim ball drawn at address, rather than only in flight.
   *
   * The default hides it because in passthrough there is a real ball on the mat
   * and two balls in the same place reads badly. Return true whenever that
   * assumption does not hold — a virtual club, or practice shots with no
   * monitor — and the player gets something to address.
   */
  ballVisibleAtRest?: (stage: MrStage) => boolean;

  /**
   * The club currently in the player's hands, or undefined to fall back to the
   * practice preset. Consulted on every repaint — see `MrStage.clubLabel` — so
   * a page can answer from whatever it already uses to decide the club is live,
   * rather than keeping a second copy in step.
   */
  clubLabel?: (stage: MrStage) => string | undefined;

  /** Extra objects to hide from the chase-cam pass. */
  extraTvHides?: (stage: MrStage) => (THREE.Object3D | undefined)[];
};

export type HeadPose = { position: THREE.Vector3; quaternion: THREE.Quaternion };

export const statusEl = () => document.getElementById('mr-status');

export class MrStage {
  config: MrStageConfig;
  params: URLSearchParams;
  setupData!: OpenGolfSim.SetupData;

  timer = new THREE.Timer();
  scene!: THREE.Scene;
  renderer!: FuseRenderer;
  camera!: THREE.PerspectiveCamera;
  /** Anchor-driven. Sim frame: tee at origin, +Z downrange. */
  worldRoot!: THREE.Group;
  meshLoader!: MeshLoader;

  golfBall?: GolfBall;
  contactShadow?: ContactShadow;
  currentPlayer?: CoursePlayer;

  xrSession?: FuseXRSession;
  anchorController?: AnchorController;
  xrControls?: XRShotControls;
  worldMask?: WorldMask;
  controllerRays?: ControllerRays;
  roomBounds?: RoomBounds;
  controls?: CourseKeyboardControls;

  statsPanel?: ShotStatsPanel;
  groundHud?: GroundStatsHUD;
  hint?: HeadLockedHint;
  wristMenu?: WristMenu;
  actionMenu?: ActionMenu;
  infoPanel?: InfoPanel;
  shotTv?: ShotTv;
  chaseCamera?: ShotPerspectiveCamera;

  audioPlayer?: AudioPlayer;
  impactSound?: ImpactSound;
  windAmbience?: WindAmbience;

  /** Sim-frame tee and aim. A course page re-poses its own root to match these. */
  startPoint = new THREE.Vector3(0, 0, 0);
  aimPoint = new THREE.Vector3(0, 0, 200);
  /**
   * Tee and aim in reference space (identity-equal to the sim frame until the
   * world is anchored). The chase camera is unparented, so it must be fed
   * world-space poses — Object3D.lookAt treats its target as world space,
   * which would silently misaim a camera positioned in worldRoot-local space.
   */
  teeWorld = new THREE.Vector3(0, 0, 0);
  aimWorld = new THREE.Vector3(0, 0, 200);

  bridgeUrl = DEFAULT_BRIDGE_URL;
  bridgeConnected = false;
  /**
   * Latched once the bridge socket has opened at least once. AppBridge emits
   * bridgeStatus(false) on a failed first attempt exactly as it does on a drop,
   * so "never connected" is only distinguishable by remembering.
   */
  bridgeEverConnected = false;
  /** A real launch-monitor shot has arrived this session. */
  monitorLive = false;
  /** ?testshots=1|0 — force practice shots on/off regardless of monitor. */
  testShotsOverride?: boolean;

  windowMode: 'auto' | 'manual' | 'off' = 'auto';
  windowRect?: WindowRect;
  /** The auto solve landed for the current anchor — stop polling. */
  windowSolved = false;
  /** Yaw from the anchored event, needed by the plane solve. */
  anchorYaw = 0;

  tvMode: 'auto' | 'always' | 'off' = 'auto';
  tvModeFromUrl: 'auto' | 'always' | 'off' = 'auto';
  tvChasing = false;
  tvFrame = 0;

  /** ?wind=mph[,deg] — deg 0 = tailwind, 180 = into, 90 = pushes right. */
  windMph = 0;
  windDeg = 0;

  simMode = false;
  maskMode: 'punch' | 'dither' = 'punch';
  confirmRequested = false;
  pendingShot?: OpenGolfSim.Shot;
  reanchorArmedUntil?: number;
  /** Which panel a controller ray is currently resting on, if any. */
  rayTarget?: 'menu' | 'info' | 'actions' | 'extra';

  handedSide: 1 | -1 = 1;
  perfHud = false;
  frameEmaMs = 13;
  lastPerfCheckAt = 0;
  foveation = 0.4;
  /**
   * The floor the perf governor may recover down to — the session's configured
   * starting foveation, not zero. Everything above this was bought by a frame
   * budget overrun and can be given back; this much is a deliberate choice.
   */
  readonly baseFoveation = 0.4;
  /** The practice-shot preset — what a pinch fires when no club is swung. */
  presetName: string;
  /** The XRFrame being drawn, for pages that pose input sources themselves. */
  currentFrame?: XRFrame;

  wristPalmOffset?: number;
  wristDistalOffset?: number;

  /**
   * Club the connector last reported selecting, if it reports one at all.
   *
   * Undefined until something says otherwise, which is what lets `clubLabel`
   * fall through to the practice preset — a monitor that never announces its
   * club leaves the old behaviour exactly as it was.
   */
  monitorClub?: string;

  #headMidpoint = new THREE.Vector3();
  #ballWorldPos = new THREE.Vector3();
  #landingPos = new THREE.Vector3();

  constructor(config: MrStageConfig) {
    this.config = config;
    this.presetName = config.defaultPreset;
    this.params = new URLSearchParams(window.location.search);
  }

  // ---------------------------------------------------------------- status --

  /**
   * Practice shots are allowed until a real monitor actually delivers a shot —
   * a connected bridge alone doesn't count, because FUSE Link is always
   * connected on the headset even with no monitor attached.
   */
  testShotsAllowed(): boolean {
    return this.testShotsOverride ?? !this.monitorLive;
  }

  /**
   * What to show in the club slot — the one answer every panel uses.
   *
   * A club in the player's hands beats a practice preset: while you are swinging
   * a real attachment, the preset is not what the next shot will be. Derived on
   * every read rather than stored, because a club is only equipped while it is
   * actually gripped — a stored field would have to be cleared from every path
   * that can put the club down, and would be wrong the moment one was missed.
   *
   * Everything that paints that slot goes through here, which is what stops the
   * wrist panel and the stats panel disagreeing: the wrist panel read the
   * preset and never saw a club at all, while the perf rewriter painted the
   * preset back over the club on the stats panel seconds after it appeared.
   */
  clubLabel(): string {
    // Order of authority: what the launch monitor's own software says is
    // selected beats a club held in the hand, which beats the practice preset.
    // When a monitor is driving, the player chose the club over there and FUSE
    // disagreeing with it is worse than FUSE not naming one at all.
    return this.monitorClub ?? this.config.clubLabel?.(this) ?? this.presetName;
  }

  readyHintText(): string {
    const custom = this.config.readyHint?.(this);
    if (custom) return custom;
    if (!this.testShotsAllowed()) return 'Ready — hit a real ball';
    return this.xrControls?.hasHandInput === false
      ? 'Ready — trigger for a practice shot'
      : 'Ready — pinch for a practice shot';
  }

  /**
   * One source of truth for launch-monitor state, shared by the wrist chip and
   * the side panel so the two can never disagree.
   *
   * The page genuinely cannot tell a monitor that is attached but unswung from
   * no monitor at all: Open Connect heartbeats are answered at the bridge's TCP
   * layer and never reach the browser, so only a real shot proves a monitor is
   * live.
   */
  launchMonitorState = (): WristMenuStatus => {
    if (this.monitorLive) return { text: 'Monitor live', tone: 'ok' };
    if (this.bridgeConnected) return { text: 'Bridge up · no shots yet', tone: 'warn' };
    if (this.bridgeEverConnected) return { text: 'Reconnecting…', tone: 'warn' };
    return { text: 'No bridge — see setup', tone: 'bad' };
  };

  /**
   * Why the bridge URL isn't working, appended to it in the help panel.
   *
   * Only shown for a socket that has *never* connected: once one has, a drop is
   * a drop and `bridgeEverConnected` already says "Reconnecting…". This exists
   * because a refused WebSocket is otherwise completely silent — no
   * interstitial, no dialog, nothing but a status that never turns green — so
   * the page has to volunteer the diagnosis itself.
   */
  bridgeUrlNote(): string {
    if (this.bridgeConnected || this.bridgeEverConnected) return '';
    if (this.bridgeUrl.startsWith('ws://') && window.location.protocol === 'https:') {
      return '  ⚠ insecure socket on a secure page — the browser blocks this outright';
    }
    if (this.bridgeUrl === DEFAULT_BRIDGE_URL) {
      // The default is same-origin, so this means whoever served the page isn't
      // running a bridge — the hosted site, typically. Practice shots still work.
      return '  ⚠ this page was not served by a bridge — open http://<pi-ip>:8080';
    }
    return '  ⚠ unreachable saved override — ?bridge=default forgets it';
  }

  /** Push the current status to the side panel. The chips pull it themselves. */
  refreshMonitorStatus() {
    this.statsPanel?.setStatusLabel(this.launchMonitorState().text);
  }

  /** Human description of the wind for hints/help ("12 mph, into · L→R"). */
  windLabel(): string {
    if (this.windMph <= 0) return 'calm';
    const deg = ((this.windDeg % 360) + 360) % 360;
    const along = Math.cos(THREE.MathUtils.degToRad(deg));
    const across = -Math.sin(THREE.MathUtils.degToRad(deg));
    const parts: string[] = [];
    if (along > 0.35) parts.push('helping');
    else if (along < -0.35) parts.push('into');
    if (across < -0.35) parts.push('L→R');
    else if (across > 0.35) parts.push('R→L');
    return `${this.windMph} mph ${parts.join(' · ') || 'swirling'}`;
  }

  /**
   * Wind, in the sim frame the ball flies in (tee origin, +Z downrange):
   * deg 0 = tailwind (blowing downrange), 180 = into the player,
   * 90 = pushing the ball toward the player's RIGHT (−X in the sim frame).
   */
  applyWind() {
    const physics = this.golfBall?.physics;
    if (!physics) return;
    const mps = UnitConversions.milesPerHourToMetersPerSecond(this.windMph);
    const rad = THREE.MathUtils.degToRad(this.windDeg);
    physics.wind.set(-Math.sin(rad) * mps, 0, Math.cos(rad) * mps);
    this.windAmbience?.setWind(this.windMph);
  }

  // ----------------------------------------------------------------- panels --

  /** Any pokeable panel is up, so positional input owns the hands. */
  panelOpen = (): boolean => {
    return !!this.wristMenu?.isOpen || !!this.infoPanel?.isOpen || !!this.actionMenu?.isOpen;
  };

  /**
   * Close every pointable surface.
   *
   * Any flow that needs the trigger for ITSELF has to do this. An open panel
   * claims the trigger twice over — as a click on whatever the ray is resting
   * on, and via the `panelOpen()` veto that stops a stray pinch becoming a shot
   * — so leaving one up strands the flow with no way to confirm. Re-placing the
   * tee hit exactly that: it is reached from a menu row, so the menu that
   * launched it was still up, swallowing the very trigger pull meant to set the
   * tee down.
   */
  closePanels() {
    this.infoPanel?.close();
    this.wristMenu?.close();
    this.actionMenu?.close();
  }

  /**
   * Frame (or unframe) the virtual world into a window. The window plane
   * doubles as the portal plane, so the two move together; dropping the window
   * restores the classic infinite portal at its default distance.
   */
  applyWindow(rect: WindowRect | null, announce = false) {
    const mask = this.worldMask;
    if (!mask) return;
    mask.setWindow(rect);
    mask.uPortalDist.value = rect ? rect.dist : DEFAULT_PORTAL_DIST;
    if (rect && announce) {
      // Self-dismissing: the solve lands once per anchor, so without a hold this
      // line would sit in front of the player for the rest of the session.
      this.hint?.setText(
        `Window framed to your room — ${(rect.left + rect.right).toFixed(1)} m wide, ${rect.top.toFixed(1)} m tall`,
        WINDOW_HINT_SECONDS,
      );
    }
  }

  /** Audible "shot registered" cue — the player is watching the real ball, not
   *  the HUD, when the monitor reports. Plain oscillator: no asset needed. */
  playShotBlip() {
    const ctx = this.audioPlayer?.context;
    if (!ctx || ctx.state !== 'running') return;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.frequency.value = 880;
    gain.gain.setValueAtTime(0.15, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.12);
    osc.connect(gain).connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.13);
  }

  // --------------------------------------------------------------------- TV --

  /**
   * Point the chase camera at a tee→aim frame in reference space. Defaults to
   * the sim frame, which is what applies in the desktop preview and after a
   * session ends (worldRoot back at identity).
   */
  updateChaseFrame(tee: THREE.Vector3 = this.startPoint, aim: THREE.Vector3 = this.aimPoint) {
    this.teeWorld.copy(tee);
    this.aimWorld.copy(aim);
    // Establishes shotDirection, which track() needs to sit behind the ball
    this.chaseCamera?.setPositions(this.teeWorld, this.aimWorld);
  }

  /** Stop driving the TV. Without `hide` the last rendered frame stays up. */
  stopTvChase(options: { hide?: boolean } = {}) {
    this.tvChasing = false;
    this.chaseCamera?.setTracking(false);
    if (options.hide) this.shotTv?.setVisible(false);
  }

  /** Whether this shot is worth putting on the TV. */
  tvShouldShow(shot: OpenGolfSim.Shot): boolean {
    if (this.tvMode === 'off') return false;
    if (!this.shotTv?.isAvailable || !this.shotTv.isPlaced) return false;
    if (this.tvMode === 'always') return true;
    return (shot.ballSpeed ?? 0) >= TV_MIN_BALL_SPEED_MPH;
  }

  // ------------------------------------------------------------------ shots --

  /** Which impact voice a monitor shot should use — the bridge doesn't say. */
  static voiceForShot(shot: OpenGolfSim.Shot): ImpactVoice {
    if (shot.verticalLaunchAngle < 1) return 'putt';
    if (shot.ballSpeed >= 135) return 'wood';
    if (shot.ballSpeed >= 85) return 'iron';
    return 'wedge';
  }

  /**
   * Rapid-fire flow: a real launch-monitor shot cuts the in-flight sim short
   * (the player has already hit another ball — Golf+/GSPro behavior); practice
   * shots fired while one is in flight are queued instead of dropped.
   */
  launchShot = (shot: OpenGolfSim.Shot, source: 'bridge' | 'local' = 'local') => {
    if (!shot.ballSpeed) return;
    // While presenting, shots only make sense once the world is on the ball
    if (this.renderer?.isPresenting && !this.anchorController?.isAnchored) return;
    const ball = this.golfBall;
    if (!ball) return;

    if (ball.isShotActive) {
      if (source === 'bridge') {
        ball.abortShot(); // emits shotEnded → setupNextShot resets synchronously
      } else {
        this.pendingShot = shot;
        this.hint?.setText('Shot queued');
        return;
      }
    }
    ball.launchShot(shot);
    // The strike itself: per-club synthesized crack, scaled by ball speed. The
    // club is inferred from the shot — a launch monitor reports what the ball
    // did, never what hit it.
    const voice = MrStage.voiceForShot(shot);
    if (this.impactSound) this.impactSound.play(voice, shot.ballSpeed);
    else this.playShotBlip();

    // Chase-cam TV. The gate has to be a launch-time estimate from ball speed:
    // the physics integrates forward with no lookahead, so the real carry isn't
    // known until the ball is most of the way there.
    if (this.shotTv) {
      const show = this.tvShouldShow(shot);
      this.shotTv.setVisible(show);
      this.tvChasing = show;
      this.tvFrame = 0;
      if (show) this.chaseCamera?.setTracking(true, Math.min(shot.ballSpeed / 150, 1));
      else this.chaseCamera?.setTracking(false);
    }
  };

  #onShotEnded = () => {
    // Stop the offscreen pass but leave the picture up: the render target keeps
    // its last contents, giving a free freeze-frame of the landing that holds
    // until the next shot, the same way the trail's arc does.
    this.stopTvChase();
    this.config.onShotEnded?.(this);
    this.config.setupNextShot?.(this);
    try {
      app.sendShotResult({
        shot: this.golfBall?.lastShot,
        stats: this.golfBall?.stats,
        player: this.currentPlayer?.player,
        club: this.currentPlayer?.currentClub,
      });
    } catch (error) {
      console.warn('Could not send shot result', error);
    }
    // Fire a queued practice shot now that the ball is back on the tee
    const pending = this.pendingShot;
    if (pending) {
      this.pendingShot = undefined;
      this.launchShot(pending);
    }
  };

  // ------------------------------------------------------------------ build --

  async build(setupData: OpenGolfSim.SetupData) {
    this.setupData = setupData;
    this.timer.connect(document);

    const canvas = document.getElementById('canvas');
    if (!canvas || !(canvas instanceof HTMLCanvasElement)) {
      throw new Error('Unable to find canvas in HTML.');
    }

    this.renderer = new FuseRenderer({
      canvas,
      adaptive: false,
      renderMode: 'webgpu',
      xr: true,
      qualityLevel: setupData.qualityLevel ?? 0, // Low: direct render, no pipeline
    });
    await this.renderer.init();
    this.meshLoader = new MeshLoader(this.renderer);

    const player = setupData.players?.[0];
    if (!player) throw new Error('No player found in setup data');
    this.currentPlayer = new CoursePlayer(player);

    this.scene = new THREE.Scene();
    // Passthrough compositing: no sky, no fog — alpha-0 pixels show the room
    this.scene.background = null;
    this.scene.fog = null;
    this.renderer.renderer.shadowMap.enabled = false;

    // Everything virtual parents under worldRoot, built at identity so the
    // physics BVH bakes sim coordinates (tee at origin, downrange +Z). The
    // anchor drives only this matrix; physics never re-learns the world.
    this.worldRoot = new THREE.Group();
    this.worldRoot.name = 'worldRoot';
    this.worldRoot.matrixAutoUpdate = false;
    this.scene.add(this.worldRoot);

    this.camera = new THREE.PerspectiveCamera(
      70,
      (canvas.clientWidth || window.innerWidth) / (canvas.clientHeight || window.innerHeight),
      0.05,
      2000, // a full course reaches much further than the range's mountain
    );
    // Desktop preview pose — the headset overwrites this with the XR pose
    this.camera.position.set(0, 1.7, -3);
    this.camera.lookAt(0, 1, 30);
    this.scene.add(this.camera);
    window.addEventListener('resize', () => {
      if (this.renderer?.isPresenting) return;
      this.camera.aspect = window.innerWidth / window.innerHeight;
      this.camera.updateProjectionMatrix();
    });

    // Golf+-style portal: virtual world only beyond the plane downrange of the
    // ball, passthrough floor bay around the mat.
    this.worldMask = new WorldMask({ featherMode: this.maskMode });

    // Audio: the player is watching the real ball, not the HUD — sound is the
    // only channel that reliably reaches them at address
    this.audioPlayer = new AudioPlayer();
    this.impactSound = new ImpactSound(this.audioPlayer);
    this.windAmbience = new WindAmbience(this.audioPlayer);
    this.audioPlayer.load(GroundThudSound).catch(() => console.warn('Could not load thud sound'));

    // The page's world — and its ball, whose parent and terrain it owns.
    await this.config.buildContent(this);

    if (!this.golfBall) throw new Error('buildContent must set stage.golfBall');
    this.golfBall.on('shotEnded', this.#onShotEnded);
    this.golfBall.on('landed', velocity => {
      // Spatialized: a 200-yard drive should LAND 200 yards out, not in the ear
      this.golfBall!.object.getWorldPosition(this.#landingPos);
      this.audioPlayer?.playAt(GroundThudSound, this.#landingPos, Math.min(1, velocity / 30));
    });
    // Trail must survive bright, denoised passthrough: wider, brighter, and
    // excluded from tone mapping so exposure 1.0 doesn't dull it
    this.golfBall.ensureTrail();
    const trailMaterial: any = this.golfBall.trail?.line.material;
    if (trailMaterial && !Array.isArray(trailMaterial)) trailMaterial.toneMapped = false;

    this.#buildPanels();
    this.#setupXR();
    this.#setupInput();

    // Label the panel from the preset actually selected — #setupXR is what
    // builds xrControls, so this can't move up with the rest of the panel setup.
    this.presetName = this.xrControls?.currentPreset.name ?? this.config.defaultPreset;
    this.statsPanel!.setPresetLabel(this.clubLabel());
    // Place the TV at the sim origin up front so the desktop preview can show
    // it; the anchor handler re-places it on the real tee.
    this.shotTv?.placeAtTee(this.startPoint, 0, this.handedSide);
    this.updateChaseFrame();

    this.config.setupNextShot?.(this);
    await this.renderer.compile(this.scene, this.camera);
  }

  #buildPanels() {
    const scene = this.scene;

    // In-world UI, three layers: ground stat tiles downrange (the main HUD),
    // the upright side panel for preset/player info, and a head-following hint
    // line for instructions that must be readable wherever the player looks
    this.groundHud = new GroundStatsHUD({ units: this.setupData.units });
    scene.add(this.groundHud.object);
    this.statsPanel = new ShotStatsPanel({ units: this.setupData.units });
    scene.add(this.statsPanel.object);
    this.hint = new HeadLockedHint();
    scene.add(this.hint.object);

    // Chase-cam TV: across the tee at eye level, so it is never in the ball path
    // or the downrange view. Hidden until a shot qualifies.
    this.shotTv = new ShotTv();
    scene.add(this.shotTv.object);
    // Detached canvas purely so ShotPerspectiveCamera's own resize handling
    // derives the render target's 16:9 aspect rather than the window's.
    const tvCanvas = document.createElement('canvas');
    tvCanvas.width = 640;
    tvCanvas.height = 360;
    // Deliberately NOT parented to worldRoot: Object3D.lookAt treats its target
    // as a world position, so a camera positioned in worldRoot-local space would
    // aim at the wrong point once the anchor transform is non-identity.
    this.chaseCamera = new ShotPerspectiveCamera({
      canvas: tvCanvas,
      fov: 30,
      near: 0.5,
      far: 2000,
      // Low and close. Height first: the desktop default ([6, 25]) looks down at
      // the ball hard enough that the picture is all turf and no sky. Distance
      // second: the trail is 0.045 m wide with sizeAttenuation, so much past
      // ~20 m it falls below one pixel on a 640x360 target and washes out.
      cameraTrackingOffsetYZ: [1.5, 18],
      // The desktop default is 3 s of static wide shot before the chase begins,
      // which on a 1 m screen throws away the launch. 500 is the floor.
      trackingDelay: 500,
    });

    // Setup / help panel, opened from the wrist menu. Content is a callback so
    // the status line and bridge URL are current every time it redraws.
    this.infoPanel = new InfoPanel({ pages: () => this.config.helpPages(this) });
    scene.add(this.infoPanel.object);
    this.infoPanel.on('openChanged', open => {
      // The hint line sits 1.4 m ahead with depthTest off — it would punch
      // straight through the panel.
      this.hint?.setText(open ? '' : (this.anchorController?.isAnchored ? this.readyHintText() : ''));
      if (open && this.actionMenu?.isOpen) this.actionMenu.close();
    });

    this.wristMenu = new WristMenu({
      clubLabel: () => this.clubLabel(),
      status: this.launchMonitorState,
      palmOffset: this.wristPalmOffset,
      distalOffset: this.wristDistalOffset,
      buttons: this.config.wristButtons(this),
    });
    scene.add(this.wristMenu.object);
    this.wristMenu.on('select', this.onMenuSelect);

    // The controller menu, opened with B/Y. Same actions, same handler, but a
    // player holding a controller has no free finger to poke a wrist panel with,
    // so the options can be spread over sections and labelled properly.
    this.actionMenu = new ActionMenu({
      title: this.config.title,
      status: this.launchMonitorState,
      // Session-wide, so they live on the rail rather than inside a section —
      // the way out must never be behind the wrong tab
      rail: this.config.menuRail?.(this) ?? [
        { id: 'help', label: () => 'Help' },
        { id: 'reload', label: () => 'Reload' },
        { id: 'exit', label: () => 'Exit MR' },
      ],
      sections: this.config.menuSections(this),
    });
    scene.add(this.actionMenu.object);
    this.actionMenu.on('select', this.onMenuSelect);
  }

  // -------------------------------------------------------------------- XR --

  #setupXR() {
    const renderer = this.renderer;
    const scene = this.scene;

    // Compositor-latency knobs: passthrough shares the compositor with the app
    // layer, so a HELD refresh rate and GPU headroom are what keep the camera
    // feed feeling immediate. ?hz= picks the closest supported refresh rate;
    // ?fbscale= trades render resolution for headroom (0.8 ≈ 36% fewer pixels).
    const hzParam = parseFloat(this.params.get('hz') ?? '');
    const fbParam = parseFloat(this.params.get('fbscale') ?? '');
    this.xrSession = new FuseXRSession({
      renderer,
      targetFrameRate: Number.isFinite(hzParam) ? hzParam : undefined,
      framebufferScale: Number.isFinite(fbParam) ? THREE.MathUtils.clamp(fbParam, 0.5, 1.2) : undefined,
    });
    this.anchorController = new AnchorController(scene, this.worldRoot);
    this.xrControls = new XRShotControls(renderer, {
      // A shot in flight suppresses all hand gestures via the swing guard
      isBusy: () => !!this.golfBall?.isShotActive,
      initialPreset: this.config.defaultPreset,
      // Reaching across to poke a hand-mounted panel brings the wrists as close
      // together as an address grip does. Without this the guard suppresses both
      // hands and the menu closes as you reach for it. Safe because
      // pinch-to-fire is already blocked while a panel is open.
      ignoreTwoHandGrip: this.panelOpen,
      // Until the tee is placed, a confirm can only anchor — it can't launch a
      // ball — so the guard's posture terms have nothing to protect and cost the
      // player every pinch made while holding their club. Motion is still guarded.
      relaxed: () => !this.anchorController?.isAnchored,
    });
    this.roomBounds = new RoomBounds();

    // Pointing beams. Built here rather than lazily so a controller picked up
    // mid-session never has to wait on a shader compile to draw its first ray.
    this.controllerRays = new ControllerRays();
    scene.add(this.controllerRays.object);

    this.anchorController.on('stateChanged', state => {
      // Named for the gesture the player actually has: with a controller in hand
      // "pinch" is a lie, and with bare hands "trigger" is.
      const pointing = this.xrControls?.hasHandInput === false;
      const messages: Record<string, string> = pointing
        ? {
          'placing-ball': 'Point at the floor by your ball, trigger to place the tee',
          'setting-aim': 'Point downrange, trigger to aim',
        }
        : {
          'placing-ball': 'Look at your ball, pinch to place the tee',
          'setting-aim': 'Look downrange, pinch to aim',
        };
      this.hint?.setText(state === 'anchored' ? this.readyHintText() : (messages[state] ?? ''));
      if (state !== 'anchored') this.groundHud?.setVisible(false);
    });

    this.anchorController.on('hint', text => this.hint?.setText(text));

    // Everything that has to follow the tee wherever it ends up.
    this.anchorController.on('anchored', ({ position, yaw }) => {
      // positionWorld (after the worldRoot transform) lives in reference space,
      // so the mask and the yardage projection both follow the anchored tee
      const forward = new THREE.Vector3(Math.sin(yaw), 0, Math.cos(yaw));
      this.worldMask?.update(position, forward);
      this.worldMask?.setEnabled(true);
      // Window: manual rect applies immediately; auto re-solves for every fresh
      // anchor (the aim line moved, so the walls' roles may have changed too)
      this.anchorYaw = yaw;
      this.windowSolved = false;
      if (this.windowMode === 'manual') this.applyWindow(this.windowRect ?? null, true);
      else if (this.windowMode === 'off') this.applyWindow(null);
      this.statsPanel?.placeAtTee(position, yaw, this.handedSide);
      this.groundHud?.placeAtTee(position, yaw);
      // Across the tee, beyond the stats panel — the direction the player faces
      // at address, so it stays clear of the ball path and the downrange view
      this.shotTv?.placeAtTee(position, yaw, this.handedSide);
      this.updateChaseFrame(position, position.clone().addScaledVector(forward, 200));
      this.config.onTeePose?.({ position, yaw }, this);
    });

    this.xrSession.on('sessionstart', async () => {
      const session = this.xrSession!.session!;
      this.worldRoot.visible = false; // hidden until anchored
      try {
        // begin() never dead-ends: without hit-test a controller intersects its
        // own ray with the floor and gaze falls back to head-relative placement
        await this.anchorController!.begin(session);
      } catch (error) {
        console.error('Anchoring failed to start', error);
        this.hint?.setText('Anchoring unavailable — try re-entering MR');
      }
      // Compile every shader while the player is still placing the tee — the
      // alternative is a pipeline-compile hitch mid-flight of the first shot
      this.#warmupShaders().catch(error => console.warn('Shader warm-up failed', error));
    });

    this.xrSession.on('referencereset', () => {
      // Recenter with a live anchor self-corrects; without one the stored
      // transform is absolute and the world just teleported — re-place it
      if (this.anchorController?.isAnchored && !this.anchorController.hasAnchor) {
        this.hint?.setText('View recentered — pinch to re-place the tee');
        this.worldMask?.setEnabled(false);
        this.anchorController.restart();
      }
    });

    this.xrSession.on('sessionend', () => {
      // Back to the desktop preview: world at identity, mask per sim mode
      this.worldRoot.visible = true;
      this.worldRoot.matrix.identity();
      this.worldRoot.matrixWorldNeedsUpdate = true;
      this.worldMask?.setEnabled(this.simMode);
      this.worldMask?.update(this.startPoint, new THREE.Vector3(0, 0, 1));
      // The auto-solved window belonged to the session's anchor — drop it
      this.windowSolved = false;
      if (this.windowMode === 'auto') this.applyWindow(null);
      this.statsPanel?.placeAtTee(this.startPoint, 0, this.handedSide);
      this.groundHud?.placeAtTee(this.startPoint, 0);
      this.shotTv?.placeAtTee(this.startPoint, 0, this.handedSide);
      this.stopTvChase({ hide: true });
      this.updateChaseFrame();
      this.hint?.setText('');
      this.confirmRequested = false;
      this.pendingShot = undefined;
      this.monitorLive = false; // fresh session, fresh assumption
      this.refreshMonitorStatus();
      this.infoPanel?.close();
      this.xrControls?.reset();
      this.wristMenu?.reset();
      this.actionMenu?.reset();
      this.controllerRays?.reset();
      this.rayTarget = undefined;
      this.config.onSessionEnd?.(this);
      const button = document.getElementById('enter-mr') as HTMLButtonElement | null;
      if (button) button.disabled = false;
    });

    this.xrControls.on('confirm', () => {
      // A trigger pull with a ray resting on a panel is a click, not a shot
      if (this.rayTarget === 'info' && this.infoPanel?.activateHover()) return;
      if (this.rayTarget === 'menu' && this.wristMenu?.activateHover()) return;
      if (this.rayTarget === 'actions' && this.actionMenu?.activateHover()) return;
      if (this.rayTarget === 'extra' && this.config.extraRayPanel?.(this)?.activateHover()) return;
      // A pinch near an open panel is a poke gone wide — never a shot
      if (this.panelOpen()) return;
      const state = this.anchorController!.state;
      if (state === 'placing-ball' || state === 'setting-aim') {
        // XRFrame is only valid inside the animation loop — defer
        this.confirmRequested = true;
      } else if (state === 'anchored') {
        if (this.testShotsAllowed()) {
          this.xrControls!.fireCurrentPreset();
        } else {
          // A live monitor owns the shots — a stray pinch must never fire a
          // phantom ball. Say so instead of silently ignoring the gesture.
          this.hint?.setText('Monitor active — practice shots off (?testshots=1 to enable)');
        }
      }
    });

    this.xrControls.on('guardChanged', active => {
      if (!this.anchorController?.isAnchored) return;
      // Gripping the club — get out of the player's face
      this.hint?.setText(active ? '' : this.readyHintText());
    });
    this.xrControls.on('testShot', shot => this.launchShot(shot));
    this.xrControls.on('presetChanged', (_index, name) => {
      this.presetName = name;
      this.statsPanel?.setPresetLabel(this.clubLabel());
      // The side panel is off to the side — the head hint is what the player
      // actually sees while cycling presets by pinching
      this.hint?.setText(`Club: ${name}`);
    });

    // B/Y: the controller's way into the same menu the wrist panel offers hands.
    this.xrControls.on('menu', () => {
      const menu = this.actionMenu;
      if (!menu) return;
      if (menu.isOpen) {
        menu.close();
        return;
      }
      // Whatever else is up loses the trigger to the menu — one pointable
      // surface at a time (see closePanels)
      this.closePanels();
      menu.open(this.getHeadPose(!!this.renderer?.isPresenting));
    });

    this.xrControls.on('reanchor', () => {
      if (!this.anchorController?.isAnchored) return;
      // Two-step: an accidental 2 s pinch must not destroy the alignment
      const now = performance.now();
      if (!this.reanchorArmedUntil || now > this.reanchorArmedUntil) {
        this.reanchorArmedUntil = now + 5000;
        // Reached by a held pinch, a held trigger or a held squeeze — don't name
        // the gesture, since with a club in hand it isn't a pinch
        this.hint?.setText('Hold again to re-place the tee');
        return;
      }
      this.reanchorArmedUntil = undefined;
      this.worldMask?.setEnabled(false);
      this.anchorController.restart();
    });

    // Desktop-visible test shot (DOM is invisible in-session; pinch covers MR)
    document.getElementById('test-shot')?.addEventListener('click', () => {
      this.#unlockAudio();
      this.xrControls?.fireCurrentPreset();
    });

    const button = document.getElementById('enter-mr') as HTMLButtonElement | null;
    button?.addEventListener('click', async () => {
      button.disabled = true;
      // The one guaranteed user gesture — unlock audio here or never
      this.#unlockAudio();
      try {
        await this.xrSession!.start();
      } catch (error) {
        console.error('Failed to start XR session', error);
        if (statusEl()) statusEl()!.textContent = `Could not start MR session: ${error}`;
        button.disabled = false;
      }
    });
  }

  /**
   * Leave this page — reload it, or switch to a sibling mode.
   *
   * Always ends the session first: navigating out of a live immersive session
   * leaves the compositor in a bad state, which on a headset means the player
   * is stuck looking at nothing with no way back.
   *
   * The query string rides along, because the flags that matter on a headset
   * (?window=, ?hand=LH, ?units=, ?hz=, ?fbscale=) were typed once on a phone
   * keyboard and re-typing them to change mode is not a thing anyone will do.
   */
  leaveTo(path?: string) {
    const target = path
      ? new URL(path, window.location.href).pathname + window.location.search
      : window.location.href;
    (this.xrSession?.end() ?? Promise.resolve())
      .catch(() => {})
      .finally(() => { window.location.href = target; });
  }

  #unlockAudio() {
    this.audioPlayer?.unlock().then(() => {
      this.windAmbience?.start();
      this.windAmbience?.setWind(this.windMph);
    }).catch(() => {});
  }

  #setupInput() {
    this.controls = new CourseKeyboardControls({ testShots: true });
    this.controls.on('testShot', shot => this.launchShot(shot));

    // Action-menu layout preview on the desktop — G, since the panel's real
    // binding is a controller button there is no way to press here.
    window.addEventListener('keydown', event => {
      if (this.renderer?.isPresenting) return;
      if (event.code === 'KeyG') this.actionMenu?.forceOpenAt(this.camera);
    });

    // Mask tuning in the desktop preview: [ ] portal distance, - = disc size, X colorize
    window.addEventListener('keydown', event => {
      const mask = this.worldMask;
      if (!mask || !this.simMode) return;
      switch (event.code) {
        case 'BracketLeft': mask.uPortalDist.value = Math.max(0, mask.uPortalDist.value - 0.1); break;
        case 'BracketRight': mask.uPortalDist.value += 0.1; break;
        case 'Minus': mask.uDiscRadii.value.multiplyScalar(0.95); break;
        case 'Equal': mask.uDiscRadii.value.multiplyScalar(1.05); break;
        case 'KeyX': {
          const next = !(this as any).maskDebug;
          mask.debugColorize(next);
          (this as any).maskDebug = next;
          break;
        }
        case 'KeyN':
          // Window preview toggle — orbit the camera to check the parallax
          if (mask.windowEnabled) this.applyWindow(null);
          else this.applyWindow(this.windowRect ?? { dist: 3.5, left: 2.2, right: 2.2, top: 2.4 });
          break;
        case 'KeyW':
          // Layout preview: park the menu in front of the camera (no hands to poke)
          this.wristMenu?.forceOpenAt(this.camera);
          break;
        case 'KeyH':
          // Help-panel layout preview — same reason: no hands on the desktop
          this.infoPanel?.toggle(this.camera);
          break;
        default: return;
      }
      console.log(`portal=${mask.uPortalDist.value.toFixed(2)}m disc=${mask.uDiscRadii.value.x.toFixed(2)}x${mask.uDiscRadii.value.y.toFixed(2)}m`);
    });
  }

  /**
   * Compile the shaders of everything that appears later (HUD, trail, ball,
   * world) without visible flashes: force objects visible with color writes
   * off, compile, restore. compileAsync skips visible:false objects, which is
   * exactly why the trail/HUD would otherwise hitch on first appearance.
   */
  async #warmupShaders() {
    this.golfBall?.ensureTrail();

    const roots: THREE.Object3D[] = [
      this.worldRoot,
      this.groundHud!.object,
      this.statsPanel!.object,
      this.hint!.object,
      this.wristMenu!.object,
      this.actionMenu!.object,
      this.shotTv!.object,
      this.infoPanel!.object,
      // Starts hidden: a controller picked up mid-session must not hitch when
      // its beam first appears
      this.controllerRays!.object,
      ...(this.config.extraWarmupRoots?.(this) ?? []),
    ].filter(Boolean);

    const visibilityChanges: { object: THREE.Object3D; visible: boolean }[] = [];
    const materialChanges: { material: any; colorWrite: boolean }[] = [];
    const seenMaterials = new Set<any>();

    for (const root of roots) {
      root.traverse((object: any) => {
        if (!object.visible) {
          visibilityChanges.push({ object, visible: false });
          object.visible = true;
        }
        const materials = Array.isArray(object.material) ? object.material : object.material ? [object.material] : [];
        for (const material of materials) {
          if (seenMaterials.has(material)) continue;
          seenMaterials.add(material);
          materialChanges.push({ material, colorWrite: material.colorWrite });
          material.colorWrite = false;
        }
      });
    }

    try {
      await (this.renderer.renderer as any).compileAsync(this.scene, this.camera);
    } finally {
      for (const { object, visible } of visibilityChanges) object.visible = visible;
      for (const { material, colorWrite } of materialChanges) material.colorWrite = colorWrite;
    }
  }

  // ------------------------------------------------------------------ menus --

  onMenuSelect = (id: string) => {
    this.playShotBlip(); // tap feedback
    if (this.config.onSelect?.(id, this)) return;
    switch (id) {
      case 'club-prev':
        this.xrControls?.cyclePreset(-1);
        break;
      case 'club-next':
        this.xrControls?.cyclePreset(1);
        break;
      case 'reload':
        // On-headset deploy pickup. End the session first — reloading from
        // inside a live session leaves the compositor in a bad state.
        this.leaveTo();
        break;
      case 'action':
        if (this.testShotsAllowed()) {
          this.xrControls?.fireCurrentPreset();
        } else {
          this.hint?.setText('Monitor active — practice shots off (?testshots=1 to enable)');
        }
        break;
      case 'reanchor':
        // A menu tap is deliberate — no two-step confirm needed. Closing first
        // is essential with a ray-driven panel: re-placing the tee needs the
        // very trigger an open panel swallows.
        this.closePanels();
        this.worldMask?.setEnabled(false);
        this.anchorController?.restart();
        break;
      case 'units': {
        const units = this.setupData.units === 'metric' ? 'imperial' : 'metric';
        this.setupData.units = units;
        if (this.groundHud) this.groundHud.units = units;
        if (this.statsPanel) this.statsPanel.units = units;
        break;
      }
      case 'help':
        this.infoPanel?.toggle(this.getHeadPose(!!this.renderer?.isPresenting));
        break;
      case 'tv':
        if (this.tvMode === 'off') {
          // Back to whatever the URL asked for, but never back to 'off'
          this.tvMode = this.tvModeFromUrl === 'off' ? 'auto' : this.tvModeFromUrl;
        } else {
          this.tvMode = 'off';
          this.stopTvChase({ hide: true });
        }
        break;
      // The wrist menu's spare slot and the action menu's own row land together:
      // the wrist panel has no room for a dedicated one
      case 'aux':
      case 'perf':
        this.perfHud = !this.perfHud;
        if (!this.perfHud) this.statsPanel?.setPresetLabel(this.clubLabel());
        break;
      case 'exit':
        this.xrSession?.end().catch(() => {});
        break;
    }
  };

  // ------------------------------------------------------------------- loop --

  /** Head pose for billboards/hints: eye midpoint, not the left eye. */
  getHeadPose(presenting: boolean): HeadPose {
    if (!presenting) return this.camera;
    const xrCam = this.renderer.xr.getCamera();
    const cameras = xrCam.cameras;
    if (cameras?.length === 2) {
      this.#headMidpoint.copy(cameras[0].position).add(cameras[1].position).multiplyScalar(0.5);
      return { position: this.#headMidpoint, quaternion: xrCam.quaternion };
    }
    return xrCam;
  }

  /** In-session degradation: dropped frames in MR are reprojection judder
   *  (nausea) — foveation is the one lever adjustable mid-session. */
  #checkPerformance(time: number, deltaMs: number) {
    this.frameEmaMs = this.frameEmaMs * 0.95 + Math.min(deltaMs, 100) * 0.05;
    if (time - this.lastPerfCheckAt < 3000) return;
    this.lastPerfCheckAt = time;

    const session: any = this.xrSession?.session;
    const budgetMs = 1000 / (session?.frameRate ?? 72);

    /**
     * Foveation goes both ways.
     *
     * It used to only climb, so a single expensive moment — the course
     * loading, a shot in the air, someone walking in front of the passthrough
     * cameras — permanently blurred the periphery for the rest of the session
     * even once the frames were cheap again. Nothing ever gave it back.
     *
     * The recovery band is deliberately much wider than the escalation band
     * (0.75 vs 1.15 of budget) and the step down is half the size of the step
     * up. Foveation is cheap to add and expensive to be wrong about: hunting
     * between two levels every few seconds is far more noticeable than sitting
     * one notch blurrier than strictly necessary, and this only re-tightens
     * when there is real headroom rather than the moment we squeak under.
     */
    if (this.frameEmaMs > budgetMs * 1.15 && this.foveation < 1) {
      this.foveation = Math.min(1, this.foveation + 0.2);
      this.renderer.xr.setFoveation(this.foveation);
      console.warn(`Frame budget exceeded (${this.frameEmaMs.toFixed(1)}ms) — foveation → ${this.foveation.toFixed(1)}`);
    } else if (this.frameEmaMs < budgetMs * 0.75 && this.foveation > this.baseFoveation) {
      this.foveation = Math.max(this.baseFoveation, this.foveation - 0.1);
      this.renderer.xr.setFoveation(this.foveation);
      console.log(`Frame budget recovered (${this.frameEmaMs.toFixed(1)}ms) — foveation → ${this.foveation.toFixed(1)}`);
    }
    if (this.perfHud) {
      this.statsPanel?.setPresetLabel(`${this.clubLabel()} · ${this.frameEmaMs.toFixed(1)}ms`);
    }
  }

  /**
   * Drive the chase camera and draw it into the TV. This is a second full scene
   * pass, so it runs only while a qualifying shot is in the air, and only on
   * every other frame — the picture is a ~1 m screen, where 36 Hz is invisible,
   * whereas dropped frames in MR are reprojection judder.
   */
  #updateTv(delta: number, sessionVisible: boolean) {
    const tv = this.shotTv;
    const chaseCamera = this.chaseCamera;
    const ball = this.golfBall;
    if (!tv || !chaseCamera || !ball || !this.tvChasing || !sessionVisible) return;

    // The ball lives under worldRoot (or a course root inside it); the chase
    // camera is unparented, so it has to be fed the ball's world position.
    ball.object.getWorldPosition(this.#ballWorldPos);
    chaseCamera.track(delta, this.teeWorld, this.#ballWorldPos);
    // track() assigns cameraTrackingFov but never applies it (src/camera.ts:278)
    chaseCamera.updateProjectionMatrix();
    chaseCamera.updateMatrixWorld();

    this.tvFrame++;
    if (this.tvFrame % 2 !== 0) return;

    const ok = tv.render({
      renderer: this.renderer,
      scene: this.scene,
      camera: chaseCamera,
      // Reference-space UI would otherwise float inside the picture
      hide: [
        tv.object,
        this.statsPanel?.object,
        this.groundHud?.object,
        this.hint?.object,
        this.wristMenu?.object,
        this.infoPanel?.object,
        this.actionMenu?.object,
        this.controllerRays?.object,
        this.config.extraRayPanel?.(this)?.object,
        ...(this.config.extraTvHides?.(this) ?? []),
      ].filter(Boolean) as THREE.Object3D[],
      worldMask: this.worldMask,
    });
    if (!ok) this.stopTvChase({ hide: true });
  }

  /**
   * Controller rays → panel hover, and the pointer dot resting on the surface.
   *
   * The beam is drawn while a panel is up (it's the only way to click one) and
   * while the tee is being placed (the player has to see where they are
   * pointing), and otherwise hidden: a permanent laser across the mat is in the
   * way at address, and there is nothing at address for it to point at.
   */
  #updateRayPanels(delta: number, frame: XRFrame, referenceSpace: XRReferenceSpace) {
    const rays = this.controllerRays;
    const controls = this.xrControls;
    if (!rays || !controls) return;

    rays.update(frame, referenceSpace, controls.inputSources);
    this.rayTarget = undefined;

    const info = this.infoPanel;
    const actions = this.actionMenu;
    const onPanel = !!info?.isOpen || !!actions?.isOpen;
    // Placing draws the beam without any panel to hit — the beam IS the
    // feedback, and the reticle it lands on comes from AnchorController
    const placing = !!this.anchorController?.isPlacing;

    // The page's own surface is checked FIRST, and before beam visibility is
    // decided — so pointing at it lights the beam up, and looking away puts the
    // beam back down. A permanent laser across the mat is in the way at
    // address; one that appears when you aim at the map is not.
    const extra = onPanel ? undefined : this.config.extraRayPanel?.(this);
    const extraHit = extra?.updateRay(delta, rays.rays);
    if (extra && !extraHit) extra.clearPointer?.();

    rays.setVisible((onPanel || placing || !!extraHit) && rays.rays.length > 0);
    if (!onPanel) {
      if (extraHit) {
        rays.setHit(extraHit.rayIndex, extraHit.distance);
        this.rayTarget = 'extra';
      } else {
        rays.setHit();
      }
      // Still tick the action menu so its idle-close timer keeps running while
      // it's shut — its clock is the only thing that can reopen the question
      actions?.updateRay(delta, []);
      return;
    }

    // The help panel is the front-most surface whenever it's up — it's opened
    // from a menu and parked between the player and it.
    const target = info?.isOpen ? 'info' : 'actions';
    const hit = target === 'info'
      ? info!.updateRay(delta, rays.rays)
      : actions!.updateRay(delta, rays.rays);
    if (hit) {
      rays.setHit(hit.rayIndex, hit.distance);
      this.rayTarget = target;
    } else {
      rays.setHit();
    }
  }

  animate = (time: number, frame?: XRFrame) => {
    // Published for onFrame consumers that need to pose controllers themselves
    // — the teleport arc reads the target ray space off it.
    this.currentFrame = frame;
    const delta = this.timer.getDelta();
    const renderer = this.renderer;
    const presenting = renderer.isPresenting;
    const head = this.getHeadPose(presenting);

    // Headset doffed / browser hidden: don't fly the shot unseen
    const sessionVisible = !presenting
      || (this.xrSession?.session as any)?.visibilityState !== 'hidden';

    if (frame && presenting && this.xrSession?.referenceSpace) {
      const referenceSpace = this.xrSession.referenceSpace;
      if (this.confirmRequested) {
        this.confirmRequested = false;
        this.anchorController?.confirm(frame, referenceSpace);
      }
      // Input sources drive placement too, so a held controller can point the
      // reticle instead of the player's gaze
      this.anchorController?.update(frame, referenceSpace, this.xrControls?.inputSources);
      this.xrControls?.update(delta, frame, referenceSpace);
      if (this.xrControls) {
        this.wristMenu?.update(delta, this.xrControls.hands, this.xrControls.guard, head);
        this.infoPanel?.update(delta, this.xrControls.hands);
        // After the poke pass, so a ray wins when both are available: the hand
        // path clears its hover when no finger is near, then this sets it.
        this.#updateRayPanels(delta, frame, referenceSpace);
      }
      this.#checkPerformance(time, delta * 1000);
    }

    // Tracer near-fade reference. Fed as a uniform rather than read from the
    // camera, and deliberately the eye MIDPOINT: a per-eye position would fade
    // the trail at a different place in each eye.
    this.golfBall?.trail?.setViewerPosition(head.position);
    // …and every shader that needs to know where the player is: grass fades,
    // bunker and cart-path edge blends, tree billboard facing. All of them used
    // the TSL `cameraPosition` built-in, which is unusable the moment an
    // ArrayCamera shows up — see src/shaders/viewer.ts. Same midpoint, so a
    // blend edge lands in the same place in both eyes.
    setViewerPosition(head.position);
    // Same midpoint drives the window's through-the-frame projection
    this.worldMask?.setEye(head.position);
    // …and the audio listener, so spatialized landings come from the right place
    this.audioPlayer?.updateListener(head.position, head.quaternion);

    this.config.onFrame?.(delta, this, head, sessionVisible);

    // Auto window: poll the room planes until the walls/ceiling resolve. Planes
    // arrive late and Space Setup can change mid-session, so this is per-frame
    // until it lands, then never again for this anchor.
    if (
      frame && presenting
      && this.windowMode === 'auto'
      && !this.windowSolved
      && this.anchorController?.isAnchored
      && this.xrSession?.referenceSpace
    ) {
      const rect = this.roomBounds?.solve(frame, this.xrSession.referenceSpace, {
        position: this.teeWorld,
        yaw: this.anchorYaw,
      });
      if (rect) {
        this.windowSolved = true;
        this.applyWindow(rect, true);
      }
    }

    if (sessionVisible) this.golfBall?.update(delta);

    if (this.golfBall) {
      const ball = this.golfBall;
      // In MR the real ball is on the mat — showing the virtual twin on top of
      // it reads as a double ball. Render the sim ball only once it's in flight
      // and clear of the tee; the contact shadow carries the depth cue.
      //
      // Unless there is no real ball. Swinging a controller, or firing practice
      // shots with no monitor attached, leaves the player addressing bare floor
      // with nothing to aim at — which is worse than a double ball by some way.
      const inFlight = ball.isShotActive
        && ball.object.position.distanceToSquared(ball.startPoint) > 0.09;
      const atRest = this.config.ballVisibleAtRest?.(this) ?? false;
      if (presenting) ball.object.visible = inFlight || atRest;
      // The shadow follows visibility, not flight: a ball drawn at address with
      // no shadow under it reads as hovering.
      //
      // Sample the ground the ball is actually over, rather than assuming the
      // tee plane. On a course the terrain rolls, so a shadow pinned to y=0
      // sinks into every rise and hangs over every hollow. This is the ball's
      // own terrain lookup — one BVH raycast, the same one physics does.
      const shadowGround = this.contactShadow
        ? ball.physics?.getTerrainInfo(ball.object.position.x, ball.object.position.z)
        : undefined;
      this.contactShadow?.update(
        ball.object.position,
        inFlight || atRest || (!presenting && ball.isShotActive),
        shadowGround?.height ?? 0,
        shadowGround?.normal,
      );

      this.groundHud?.update(ball.stats, ball.lastShot);
      this.statsPanel?.update(ball.stats, ball.lastShot);
      this.statsPanel?.face(head.position);
      this.hint?.update(delta, head.position, head.quaternion);
    }

    this.#updateTv(delta, sessionVisible);

    renderer.render(this.scene, this.camera); // no fog arg — MR keeps scene.fog null

    this.timer.update(time);
  };

  // ------------------------------------------------------------- URL params --

  /** Read every stage-level URL knob. Pages read their own on top of this. */
  readParams() {
    const params = this.params;
    this.simMode = params.get('sim') === '1' || params.get('sim') === 'true';

    const qualityParam = params.get('quality');
    if (qualityParam) this.setupData.qualityLevel = parseInt(qualityParam, 10);
    // No host app supplies setup on the headset — let the URL override the
    // defaults (elevation feeds air density; a Denver user needs this)
    const unitsParam = params.get('units');
    if (unitsParam === 'imperial' || unitsParam === 'metric') this.setupData.units = unitsParam;
    const elevationParam = params.get('elevation');
    if (elevationParam) {
      const elevation = parseFloat(elevationParam);
      if (!Number.isNaN(elevation)) this.setupData.elevation = elevation;
    }
    // Left-handed players get the side panel across the ball, not in the swing
    this.handedSide = params.get('hand')?.toUpperCase() === 'LH' ? -1 : 1;
    this.perfHud = params.get('perf') === '1';
    const testShotsParam = params.get('testshots');
    if (testShotsParam === '1' || testShotsParam === 'true') this.testShotsOverride = true;
    else if (testShotsParam === '0' || testShotsParam === 'false') this.testShotsOverride = false;
    // Wrist-menu placement — the only way to tune it on the mat without a rebuild
    const wristOffset = parseFloat(params.get('wrist') ?? '');
    if (!Number.isNaN(wristOffset)) this.wristPalmOffset = wristOffset;
    const wristDistal = parseFloat(params.get('wristdistal') ?? '');
    if (!Number.isNaN(wristDistal)) this.wristDistalOffset = wristDistal;
    // ?tv=0 disables the chase-cam screen, ?tv=1 shows it on every shot
    const tvParam = params.get('tv');
    if (tvParam === '0' || tvParam === 'false') this.tvMode = 'off';
    else if (tvParam === '1' || tvParam === 'true') this.tvMode = 'always';
    this.tvModeFromUrl = this.tvMode;

    if (params.get('mask') === 'dither') {
      this.maskMode = 'dither'; // fallback masking, no compositor-alpha dependency
    }
    // ?wind=mph[,deg] — 0° tail, 180° into, 90° pushes right. Physics-level:
    // drag and lift act on air-relative velocity, so this shapes real flight.
    const windParam = params.get('wind');
    if (windParam) {
      const [mph, deg] = windParam.split(',').map(part => parseFloat(part));
      if (Number.isFinite(mph) && mph >= 0) {
        this.windMph = Math.min(mph, 40);
        this.windDeg = Number.isFinite(deg) ? deg : 0;
      }
    }
    // ?window=off → infinite portal; ?window=dist,left,right,top → manual frame;
    // absent → auto-frame from Space Setup planes (silently infinite without them)
    const windowParam = params.get('window');
    if (windowParam === 'off' || windowParam === '0') {
      this.windowMode = 'off';
    } else if (windowParam) {
      const parts = windowParam.split(',').map(part => parseFloat(part));
      if (parts.length === 4 && parts.every(value => Number.isFinite(value) && value > 0)) {
        this.windowMode = 'manual';
        this.windowRect = { dist: parts[0], left: parts[1], right: parts[2], top: parts[3] };
      } else {
        console.warn(`Ignoring malformed ?window='${windowParam}' — expected dist,left,right,top`);
      }
    }
  }

  /** Canvas HUDs measure and draw text at construction — Rubik must be loaded
   *  by then or every tile renders in the fallback font. */
  static async loadFonts() {
    await Promise.allSettled([
      document.fonts.load('bold 58px "Rubik"'),
      document.fonts.load('bold 52px "Rubik"'),
      document.fonts.load('bold 36px "Rubik"'),
      document.fonts.load('bold 30px "Rubik"'),
      // The help panel's body text is Regular/Medium — a different FACE, not
      // just a different size, so loading only bold would silently fall back
      document.fonts.load('400 30px "Rubik"'),
      document.fonts.load('500 30px "Rubik"'),
    ]);
  }

  /** Desktop mask preview: a fake anchor at the sim origin. */
  applySimPreview() {
    // A colorful page background behind the alpha canvas makes
    // premultiply/halo bugs visible — same compositing math as the XR layer.
    this.worldMask?.update(this.startPoint, new THREE.Vector3(0, 0, 1));
    this.worldMask?.setEnabled(true);
    // Window preview: a fake room so the parallax is testable on the desktop
    if (this.windowMode !== 'off') {
      this.applyWindow(this.windowRect ?? { dist: 3.5, left: 2.2, right: 2.2, top: 2.4 });
    }
    this.statsPanel?.placeAtTee(this.startPoint, 0, this.handedSide);
    this.groundHud?.placeAtTee(this.startPoint, 0);
    const canvas = document.getElementById('canvas') as HTMLCanvasElement;
    canvas.style.background = 'linear-gradient(135deg, #7752c4 0%, #d4525e 50%, #52d4a8 100%)';
    if (statusEl()) {
      statusEl()!.textContent = 'Desktop mask preview (?sim=1) — keys: [ ] portal, - = disc, N window, X colorize';
    }
    this.config.onSimPreview?.(this);
  }

  /**
   * Bridge URL precedence: ?bridge= wins and is REMEMBERED, then a stored
   * override, then the origin-derived default (see defaultBridgeUrl).
   *
   * A remembered override deliberately outranks the default, so a player who
   * once pointed at a bridge by hand keeps reaching it. The cost is that a
   * STALE override also outranks a perfectly good same-origin bridge — if the
   * Pi's address changes, the saved URL wins and fails silently. That's what
   * bridgeUrlNote() diagnoses, and ?bridge=default clears it.
   */
  connectBridge() {
    const bridgeParam = this.params.get('bridge');
    if (bridgeParam === 'default' || bridgeParam === 'off') {
      try { localStorage.removeItem('fuse.bridgeUrl'); } catch { /* storage disabled */ }
    } else if (bridgeParam) {
      this.bridgeUrl = bridgeParam;
      try { localStorage.setItem('fuse.bridgeUrl', bridgeParam); } catch { /* storage disabled */ }
    } else {
      let stored: string | null = null;
      try { stored = localStorage.getItem('fuse.bridgeUrl'); } catch { /* storage disabled */ }
      if (stored) this.bridgeUrl = stored;
    }
    app.on('bridgeStatus', connected => {
      this.bridgeConnected = connected;
      if (connected) this.bridgeEverConnected = true;
      this.refreshMonitorStatus();
    });
    // The connector owns the club when a monitor is driving: the player picked
    // it over there, and FUSE naming a different one is worse than naming none.
    app.on('club', club => {
      const label = club.name ?? club.code;
      if (!label || label === this.monitorClub) return;
      this.monitorClub = label;
      this.statsPanel?.setPresetLabel(this.clubLabel());
      this.hint?.setText(`Club: ${label}`);
      console.log(`[bridge] club → ${label}`);
    });

    app.connectWebSocket(this.bridgeUrl);
    this.refreshMonitorStatus();

    // AppBridge 'shot' events come from the launch-monitor bridge — real balls
    // cut an in-flight sim short rather than being dropped
    app.on('shot', shot => {
      // First real shot proves a monitor is live → practice pinches lock out
      if (!this.monitorLive) {
        this.monitorLive = true;
        this.refreshMonitorStatus();
        if (this.anchorController?.isAnchored) this.hint?.setText(this.readyHintText());
      }
      this.launchShot(shot, 'bridge');
    });
  }

  /** Start the render loop and enable Enter MR once it exists. */
  async start() {
    document.body.style.opacity = '1';
    // setAnimationLoop behaves like rAF outside a session and forwards the
    // XRFrame while presenting — one loop for desktop preview and headset
    this.renderer.renderer.setAnimationLoop(this.animate);

    // Only enable Enter MR once the loop exists — a session started earlier
    // would capture a null animation loop and render nothing
    const button = document.getElementById('enter-mr') as HTMLButtonElement | null;
    const supported = await FuseXRSession.isSupported();
    if (!this.simMode && statusEl()) {
      statusEl()!.textContent = supported
        ? 'WebXR ready'
        : 'immersive-ar not available — desktop preview only (?sim=1)';
    }
    if (button) button.disabled = !supported;
  }
}

export { sunColor };
